// The draft flag of a HOSTED text atom — the act's and the fragment's logline
// and plot rows the sheet shows above the first scene of each (#1840 item 15),
// and, since #1841 item 12, a scene's own extra loglines and plots.
//
// Those rows are not engine lines (see CONTEXT.md § Act / episode header rows),
// so the block flag — an engine fact persisted per scene as `draft_line_ids` —
// has nothing to hold them by. They are somebody else's text, and the one place
// a fact about that text belongs is the atom itself:
//
//     content_json = { …whatever the atom already keeps…, draft: { user, at } }
//
// Three contract facts, each the reason a line below exists:
//
//   - **The column arrives in several shapes.** The `?editor` responses decode
//     `content_json` into an object, the plain atom endpoints hand back the raw
//     JSON *string* it is stored as, and either can be array-wrapped (`[{…}]`,
//     `[]`). {@link readTextAtomDraft} takes all of them — the same tolerance
//     `data/sceneNoteTextAtoms.ts` and `collectContentJsonLineIds` carry.
//   - **The atom already carries things.** An act logline's `content_json` is
//     where the plan's line associations live (`paragraph_lines`, #1830), so a
//     flag is a MERGE, never a replacement — {@link withTextAtomDraft} keeps
//     every other key.
//   - **The write goes through `PATCH projects/:p/text-atoms/:id`**, sending the
//     object (server-side `nullable|array`), never the nested `acts[]` /
//     `fragments[]` payload the text itself is saved through: that one sends
//     only `content`, so the flag would never leave the browser (#1840 decision).
//
// The flag is not a document fact, so the engine's history does not restore it
// itself: the gesture that wrote it is kept as a hosted-row step, and undo
// writes the column back (ADR 0025, `hooks/fountain/useHostedRowHistory.ts`).

import type { NoteStamp } from '@/utils/fountain/noteMarker';

/** The one key this module owns inside `content_json`. */
const DRAFT_KEY = 'draft';

/** Which entity a hosted atom hangs off — the one fact a WRITE needs beyond the
 *  atom id, because an act's rows, a fragment's rows and a scene's are three
 *  different reads. Declared here, with the flag, so the data layer and the
 *  view name it once. */
export type TextAtomDraftEntity = 'act' | 'fragment' | 'scene';

/** `content_json` as it actually arrives: the decoded object from the `?editor`
 *  responses, the raw JSON string from the plain atom endpoints, either of them
 *  array-wrapped, or nothing at all. The same signature the sibling reader takes
 *  (`collectContentJsonLineIds`). */
export type ContentJsonColumn = string | object | null | undefined;

/** Whether the row is set aside, and who did it. `stamp` is `null` for a flag
 *  with no signature — a legacy `draft: true`, the same "drafted before
 *  signatures existed" case `DraftBadge` renders as the bare word. */
export interface TextAtomDraft {
    drafted: boolean;
    stamp: NoteStamp | null;
}

const LIVE: TextAtomDraft = { drafted: false, stamp: null };

/** The draft flag of an atom, from either read shape. Anything unparseable —
 *  or parseable but not an object — reads as LIVE: a row the reader cannot
 *  decide about is a row on the sheet, never a silently hidden one.
 *
 *  Asked of EVERY atom now that «which one is active» is this flag's answer
 *  (`data/activeTextAtom.ts`), including a scene logline's on every card
 *  render — hence the substring guard: JSON always quotes its keys, so a column
 *  whose text does not contain `"draft"` cannot carry the key, and the parse is
 *  skipped. A negative test only; anything that mentions it is parsed. */
export function readTextAtomDraft(raw: ContentJsonColumn): TextAtomDraft {
    if (typeof raw === 'string' && !raw.includes(`"${DRAFT_KEY}"`)) return LIVE;
    const json = parseContentJson(raw);
    if (json === null) return LIVE;
    const flag = json[DRAFT_KEY];
    if (flag === undefined || flag === null || flag === false) return LIVE;
    return { drafted: true, stamp: readStamp(flag) };
}

/**
 * The atom's `content_json` with the draft flag set to `stamp` (or cleared with
 * `null`), everything else it holds untouched.
 *
 * **Signs once.** Flagging a row that is already a signed draft keeps the first
 * signature — the engine's blocks do the same (`draftStampByLineId` is written
 * on first marking and never re-signed), so "who set this aside" cannot drift
 * to whoever touched it last. Restoring drops the key, so the next flag is a
 * new signature.
 */
export function withTextAtomDraft(raw: ContentJsonColumn, stamp: NoteStamp | null): Record<string, unknown> {
    const next = { ...(parseContentJson(raw) ?? {}) };
    if (stamp === null) {
        delete next[DRAFT_KEY];
        return next;
    }
    const existing = readTextAtomDraft(raw);
    next[DRAFT_KEY] = existing.stamp ?? stamp;
    return next;
}

/** The column as the object a write sends — unwrapped from whichever read shape
 *  it arrived in, `{}` when there is nothing readable. What an undo writes back
 *  (ADR 0025): the same object the row held, draft signature included. */
export function contentJsonObject(raw: ContentJsonColumn): Record<string, unknown> {
    return parseContentJson(raw) ?? {};
}

/** Both read shapes, plus "nothing readable here" as `null`.
 *
 *  A payload can also arrive ARRAY-wrapped (`[{…}]`, and `[]` for an empty
 *  object — PHP's json_encode of an empty associative array). Unwrapping it is
 *  not a nicety: `withTextAtomDraft` merges INTO what it reads, so a reader that
 *  saw nothing here would drop a row's line associations the first time it was
 *  set aside. Both other readers of this column do the same
 *  (`collectContentJsonLineIds`, `EditableSynopsis`), and every writer stores
 *  the unwrapped object back. */
function parseContentJson(raw: ContentJsonColumn): Record<string, unknown> | null {
    if (typeof raw === 'string') {
        if (raw === '') return null;
        try {
            return unwrap(JSON.parse(raw));
        } catch {
            return null;
        }
    }
    return unwrap(raw);
}

function unwrap(value: unknown): Record<string, unknown> | null {
    return asRecord(Array.isArray(value) ? value[0] : value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** A signature, or `null` when the flag carries none worth printing. */
function readStamp(flag: unknown): NoteStamp | null {
    const record = asRecord(flag);
    if (record === null) return null;
    const user = typeof record.user === 'string' ? record.user : '';
    const at = typeof record.at === 'string' ? record.at : '';
    return user === '' && at === '' ? null : { user, at };
}
